/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { END } from 'redux-saga';
import {
    fork, take, call, put, select,
} from 'redux-saga/effects';
import mbpLogger from '@module-logger/mbp';

import memberDucks from '../../../Member/ducks';
import * as updateCartActions from './UpdateCart-Actions';
import { commonSelectors } from '../../../Common';
import { showLoadingMessageActions } from '../../../Common/ducks/ShowLoadingMessage';
import { errorActions, errorOperations } from '../../../Common/ducks/Error';
import { retrieveCart } from '../../Cart-Operations';
import { checkoutOperations } from '../../../Checkout/ducks/Checkout';
import { callHandleCartFailure } from '../../Cart-Actions';
import orderClient from '../../../../../apis/checkout-apis/orderClient';
import { loadError } from '../../../Common/ducks/Error/Error-Actions';
import { processOrderItemError } from '../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import cartServices from '../../../../../apis/cart-service-apis/cartServices';

const {
    auth: {
        authOperations,
    },
} = memberDucks;

const { checkJWT } = authOperations.workers;
const { prepareRetreiveCart } = checkoutOperations;

function* updateCartError(action, exception) {
    if (action.updateOrderProps.updateType === 'pmallShippingMethod') {
        const response = exception?.response || {};
        const genericErrorMessage = 'We are sorry, there seems to be a technical issue, please try again';
        let errorMessage = response?.data?.detailedError?.errorMessage || response?.data?.errorMessage;
        const errorMessageList = response?.data?.detailedError?.validationErrors || [];

        if (!errorMessage) {
            errorMessage = genericErrorMessage;
        }

        yield put(loadError({
            errMsg: { [action.data.orderItemId]: { errorMessage, errorMessageList } },
            errType: 'pmallShippingMethod',
        }));
    }
}

const updateCart = (reqObj) => orderClient.updateCart(
    reqObj.wcEnv,
    reqObj.jwtToken,
    reqObj.orderItemId,
    reqObj.quantity,
    reqObj.updateDeliveryDate,
    null,
    reqObj.orderId,
    reqObj?.personalization || null,
);

function* updateOrder(action) {
    try {
        // Show Site Loader
        yield put(showLoadingMessageActions.updateShowLoadingMessage());

        // Clear Error
        yield put(errorActions.clearErrors({
            errType: 'cart',
            field: 'error',
        }));
        yield put(errorActions.clearErrors({
            errType: 'payment',
            field: action.data.orderItemId,
        }));
        yield put(errorActions.clearErrors({
            errType: 'payment',
            field: 'error',
        }));

        yield put(loadError({
            errMsg: { [action.data.orderItemId]: '' },
            errType: 'pmallShippingMethod',
        }));

        const jwtToken = yield call(checkJWT);
        const orderId = yield select(commonSelectors.getOrderId);

        switch (action.updateOrderProps.updateType) {
            case 'delivery':
            case 'pmallShippingMethod': {
                const cartId = yield select(commonSelectors.getOrderId);
                const deliveryDateObj = action.data?.updateDeliveryDate || {};
                const cartObj = {
                    env: {},
                    jwtToken,
                    cartId,
                    cartItemId: action.data.orderItemId,
                    deliveryDate: deliveryDateObj.deliveryDate || null,
                    deliverySLA: deliveryDateObj.deliverySLA || null,
                    deliveryDateType: deliveryDateObj.deliveryDateType || null,
                    deliverRangeStartDate: deliveryDateObj.deliverRangeStartDate || null,
                };

                if (deliveryDateObj.calendarStartDate) {
                    cartObj.deliverRangeStartDate = deliveryDateObj.calendarStartDate;
                }

                if (action.data.zipCode) {
                    cartObj.zipCode = action.data.zipCode;
                }

                if (action.data.locationType) {
                    cartObj.locationType = action.data.locationType;
                }

                if (deliveryDateObj.holidayCode) {
                    cartObj.holidayCode = deliveryDateObj.holidayCode;
                }

                if (deliveryDateObj.attributes) {
                    cartObj.attributes = deliveryDateObj.attributes;
                }

                if (deliveryDateObj.flex?.flexOptionId) {
                    cartObj.flexOptionId = deliveryDateObj.flex?.flexOptionId;
                    cartObj.flexOptionKey = deliveryDateObj.flex?.flexOptionKey || null;
                    if (deliveryDateObj.flex?.flexVariantId) {
                        cartObj.flexVariantId = deliveryDateObj.flex?.flexVariantId || null;
                    }
                }

                yield call(cartServices.updateCartItemDeliveryInfo, cartObj);

                yield call(prepareRetreiveCart, action.updateOrderProps.page);
                yield call(retrieveCart);
                if (action.callback) {
                    yield call(action.callback);
                }
                break;
            }
            case 'quantity': {
                const reqObj = {
                    wcEnv: {},
                    jwtToken,
                    orderItemId: action.data.orderItemId,
                    quantity: action.data.quantity,
                    orderId,
                };
                yield call(updateCart, reqObj);
                yield call(prepareRetreiveCart, action.updateOrderProps.page);
                yield call(retrieveCart);
                break;
            }
            case 'personalization': {
                const reqObj = {
                    wcEnv: {},
                    jwtToken,
                    orderItemId: action.data.orderItemId,
                    quantity: null,
                    updateDeliveryDate: {},
                    orderId,
                    personalization: action.data.personalization,
                };

                yield call(updateCart, reqObj);
                yield call(prepareRetreiveCart, action.updateOrderProps.page);
                yield call(retrieveCart);
                break;
            }
            default:
                return;
        }
        if (action.data.callback) {
            yield call(action.data.callback, false);
        }
        // Hide Site Loader
        yield put(showLoadingMessageActions.flagOffShowLoadingMessage());
    } catch (ex) {
        yield put(showLoadingMessageActions.flagOffShowLoadingMessage());
        yield put(callHandleCartFailure(ex));
        yield call(errorOperations.getError, ex, 'cart');
        yield call(updateCartError, action, ex);
        yield call(retrieveCart);
        yield call(action.data.callback, false);
        yield fork(processOrderItemError, {
            data: {
                orderItemId: action.data.orderItemId,
                errorFeatureType: 'recipient',
                errorValues: ex.response?.data,
            },
        });

        mbpLogger.logError({
            action,
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            function: 'updateOrder',
            jsError: ex,
            message: 'Update Operation: update order failed.',
        });
    }
}

function* updateOrderWatcher() {
    let action = yield take(updateCartActions.updateOrder().type);
    while (action !== END) {
        yield call(updateOrder, action);
        action = yield take(updateCartActions.updateOrder().type);
    }
}

const watchers = [
    fork(updateOrderWatcher),
];

export { watchers };

export default {};
